import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpContext, HttpContextToken } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { EModelApiPaths } from '../enums/EModelApiPaths';

/**
 * Marks an HTTP request as the system AM-token bootstrap call so the auth
 * interceptor knows to skip it (it has no token to attach yet).
 */
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

interface IAMTokenItem {
    token: string;
    refresh_token?: string;
    expires_in?: number;
}

interface IGetAMTokenResponse {
    success?: boolean;
    data?: IAMTokenItem | IAMTokenItem[];
}

@Injectable({ providedIn: 'root' })
export class ApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.T_SHIRT_CAFE_BE_HOST_PORT_SITES;
    private readonly userPath = environment.USER_PATH;
    private readonly ins = environment.INS;
    private readonly db = environment.DB;

    get customApiBase() {
        return `${this.baseUrl}${EModelApiPaths.CUSTOM_API_BASE}/${this.userPath}`;
    }

    get schemaBase() {
        return `${this.baseUrl}${EModelApiPaths.SYSTEM_API_BASE}/${this.userPath}/${this.ins}/${this.db}`;
    }

    /**
     * Calls API Maker's system token endpoint to issue an AM admin token using
     * the hardcoded `default/default` credentials. The endpoint is
     * /api/system-api/{userPath}/token and accepts a single auth-token-info
     * object as the body. Returns the token string or null on failure.
     */
    async getAMToken(): Promise<string | null> {
        const url = `${this.baseUrl}${EModelApiPaths.SYSTEM_TOKEN_BASE}/${this.userPath}/token`;
        const body = {
          u: 'default',
          p: 'default',
        };
        try {
            const res = await firstValueFrom(
                this.http.post<IGetAMTokenResponse>(url, body, {
                    context: new HttpContext().set(SKIP_AUTH, true),
                }),
            );
            const data = res?.data;
            if (Array.isArray(data)) return data[0]?.token || null;
            return data?.token || null;
        } catch (e) {
            console.error('Failed to fetch AM token', e);
            return null;
        }
    }

    callCustomApi<T>(
        path: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
        body?: unknown,
        queryParams?: Record<string, unknown>,
        headers?: Record<string, string>,
    ): Observable<T> {
        const url = `${this.customApiBase}${path}`;
        let params = new HttpParams();
        if (queryParams) {
            Object.entries(queryParams).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
            });
        }
        const options: { params: HttpParams; headers?: Record<string, string> } = { params };
        if (headers) options.headers = headers;
        switch (method) {
            case 'POST': return this.http.post<T>(url, body, options);
            case 'PUT': return this.http.put<T>(url, body, options);
            case 'DELETE': return this.http.delete<T>(url, options);
            default: return this.http.get<T>(url, options);
        }
    }

    schemaQuery<T>(collection: string, body: unknown): Observable<T> {
        return this.http.post<T>(`${this.schemaBase}/${collection}/query`, body);
    }
}
